import React, { useState, useEffect } from "react";
import { CheckCircle, Clock, Download, Users } from "lucide-react";
import { getVendorSignatures } from "../../../services/EventDocumentService";

const STATUS_CONFIG = {
  signed: { label: "Signed", color: "text-green-700 bg-green-100", icon: CheckCircle },
  pending: { label: "Pending", color: "text-yellow-700 bg-yellow-100", icon: Clock },
};

/**
 * Shows per-vendor signing status for a single document.
 * Props:
 *   eventId       — string
 *   document      — requiredDocument object
 *   assignedVendors — [{ id, vendorName }]
 */
const DocumentSigningStatus = ({ eventId, document, assignedVendors = [] }) => {
  const [signatures, setSignatures] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      const result = await getVendorSignatures(eventId, document.id);
      if (result.success) setSignatures(result.signatures);
      setLoading(false);
    };
    load();
  }, [eventId, document.id]);

  const signedIds = new Set(signatures.filter((s) => s.status === "signed").map((s) => s.vendorId));

  // Build rows: assigned vendors + any who signed but weren't in the list
  const vendorRows = assignedVendors.map((v) => ({
    id: v.id,
    name: v.vendorName || v.id,
    status: signedIds.has(v.id) ? "signed" : "pending",
    signedPdfDownloadURL: signatures.find((s) => s.vendorId === v.id)?.signedPdfDownloadURL,
    completedAt: signatures.find((s) => s.vendorId === v.id)?.completedAt,
  }));

  const signedCount = vendorRows.filter((r) => r.status === "signed").length;
  const total = vendorRows.length;

  if (loading) {
    return <p className="text-sm text-grey-400 py-2">Loading signatures...</p>;
  }

  if (total === 0) {
    return (
      <div className="text-center py-6">
        <Users className="w-8 h-8 text-grey-300 mx-auto mb-2" />
        <p className="text-sm text-grey-500">No vendors assigned yet.</p>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm font-medium text-grey-700">
          {signedCount} of {total} signed
        </p>
        <div className="w-32 h-2 bg-grey-200 rounded-full overflow-hidden">
          <div
            className="h-full bg-green-500 rounded-full transition-all"
            style={{ width: `${total > 0 ? (signedCount / total) * 100 : 0}%` }}
          />
        </div>
      </div>

      <div className="space-y-2">
        {vendorRows.map((row) => {
          const cfg = STATUS_CONFIG[row.status];
          const Icon = cfg.icon;
          return (
            <div key={row.id} className="flex items-center justify-between py-2 px-3 bg-grey-50 rounded-lg">
              <div className="flex items-center gap-3">
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${cfg.color}`}>
                  <Icon className="w-3 h-3" />
                  {cfg.label}
                </span>
                <span className="text-sm text-grey-800">{row.name}</span>
              </div>
              {row.signedPdfDownloadURL && (
                <a
                  href={row.signedPdfDownloadURL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 text-xs text-pebble-red hover:underline"
                >
                  <Download className="w-3 h-3" />
                  Download
                </a>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default DocumentSigningStatus;
